import { useEffect, useMemo, useState } from 'react';
import './index.scss';

const configurations = {
    flow: {
        title: 'AI混合模型放流水質預測(水量調控模擬)',
        inputLabel: '1小時後廠外進流水預測值',
        inputPrefix: '導電度',
        inputUnit: 'μS/cm',
        inputDefault: 1300,
        inputMin: 1200,
        inputMax: 3000,
        inputHint: '輸入範圍：1,200 - 3,000 μS/cm',
        optionLabel: '處理水量',
        rangeMin: 26000,
        rangeMax: 30000,
        rangeStep: 100,
        rangeDefault: 26000,
        currentValue: '26,000 噸/日',
        formatRange: (value) => `${Number(value).toLocaleString()} 噸/日`,
        calculate: (conductivity, flow) => {
            const base = 50 + 0.025 * (conductivity - 1200);
            const slope = 0.0166667 + 0.00000115741 * (conductivity - 1200);
            return Math.round((base + slope * (flow - 26000)) * 10) / 10;
        },
    },
    aeration: {
        title: 'AI混合模型放流水質預測(曝氣量調控模擬)',
        inputLabel: '平均COD濃度',
        inputPrefix: '',
        inputUnit: 'mg/L',
        inputDefault: 75,
        inputMin: 60,
        inputMax: 85,
        inputHint: '在設計值170 mg/L之50%（85 mg/L）以下具節能空間',
        inputRangeHint: '輸入範圍：60 - 85 mg/L',
        optionLabel: '曝氣量',
        rangeMin: 50,
        rangeMax: 100,
        rangeStep: 1,
        rangeDefault: 100,
        currentValue: '100%',
        formatRange: (value) => `${value}%`,
        calculate: (cod, aeration) => {
            const baseline = 0.0146667 * cod * cod - 0.84667 * cod + 16;
            return Math.round((baseline + 2 * (100 - aeration)) * 10) / 10;
        },
    },
};

const WaterQualitySimulation = ({ mode }) => {
    const config = useMemo(() => configurations[mode] || configurations.flow, [mode]);
    const [inputValue, setInputValue] = useState(config.inputDefault);
    const [rangeValue, setRangeValue] = useState(config.rangeDefault);
    const [result, setResult] = useState(null);

    useEffect(() => {
        setInputValue(config.inputDefault);
        setRangeValue(config.rangeDefault);
        setResult(null);
    }, [config]);

    const normalizeInputValue = (value) => {
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
            return config.inputDefault;
        }

        return Math.min(config.inputMax, Math.max(config.inputMin, numericValue));
    };

    const runSimulation = () => {
        const normalizedInput = normalizeInputValue(inputValue);
        setInputValue(normalizedInput);
        const prediction = config.calculate(normalizedInput, Number(rangeValue));
        setResult({
            prediction,
            isAllowed: prediction <= 100,
        });
    };

    return (
        <main className="simulation-page">
            <section className="simulation-card">
                <h1>{config.title}</h1>

                <div className="simulation-input-box">
                    <div className="simulation-field-label">{config.inputLabel}</div>
                    <div className="simulation-field-value">
                        {config.inputPrefix && <span className="input-prefix">{config.inputPrefix}</span>}
                        <input
                            type="number"
                            min={config.inputMin}
                            max={config.inputMax}
                            step="1"
                            value={inputValue}
                            onChange={(event) => {
                                setInputValue(event.target.value);
                                setResult(null);
                            }}
                            onBlur={(event) => {
                                setInputValue(normalizeInputValue(event.target.value));
                            }}
                        />
                        <span>{config.inputUnit}</span>
                    </div>
                    <div className="simulation-hint">{config.inputHint}</div>
                    {config.inputRangeHint && (
                        <div className="simulation-range-hint">{config.inputRangeHint}</div>
                    )}
                </div>

                <div className="simulation-panel">
                    <div className="simulation-panel-label">調整選項：{config.optionLabel}</div>
                    <div className="simulation-slider-row">
                        <input
                            type="range"
                            min={config.rangeMin}
                            max={config.rangeMax}
                            step={config.rangeStep}
                            value={rangeValue}
                            onChange={(event) => {
                                setRangeValue(Number(event.target.value));
                                setResult(null);
                            }}
                        />
                        <div className="simulation-range-value">
                            <strong>{config.formatRange(rangeValue)}</strong>
                            {mode === 'flow' && (
                                <span>({(Number(rangeValue) / 24).toFixed(1)} 噸/時)</span>
                            )}
                        </div>
                    </div>
                    <div className="simulation-current">目前實際運轉值：{config.currentValue}</div>
                    <button type="button" onClick={runSimulation}>執行模擬</button>

                    {result && (
                        <div className={`simulation-result ${result.isAllowed ? 'allowed' : 'denied'}`}>
                            <div>17小時後廠內放流水質預測值</div>
                            <strong>COD {result.prediction} mg/L</strong>
                            <div>
                                {result.isAllowed
                                    ? '低於放流標準，確認符合相關作業規範後，建議可執行'
                                    : '高於放流標準，建議不可執行'}
                            </div>
                        </div>
                    )}
                </div>

                <p className="simulation-disclaimer">
                    本預測結果僅供操作參考，實際執行前請依現場作業規範確認
                </p>
            </section>
        </main>
    );
};

export default WaterQualitySimulation;
